/**
 * Site footer with brand info, email contact link, copyright line,
 * and a SoundCloud external link. Uses scroll-triggered fade-in animations.
 */
import { forwardRef } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowUpRight } from "lucide-react";

const Footer = forwardRef<HTMLElement>((_, ref) => {
  return (
    <footer ref={ref} className="bg-background py-20 mx-6 md:mx-16">
      <div className="border-t border-border pt-20">
        <div className="mx-auto max-w-6xl">
        <div className="grid gap-16 md:grid-cols-2">
          {/* Left - Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="font-display text-4xl tracking-tight text-foreground mb-4">
              Thursday Jam
            </h3>
            <p className="text-base text-muted-foreground max-w-xs leading-relaxed">
              A family band from Berlin, Germany.
              <br />
              Blues rock, modern roots, and raw energy.
            </p>
          </motion.div>

          {/* Right - Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="md:text-right"
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Get in touch
            </p>
            <a
              href="mailto:music@thursdayjam.rocks"
              className="group inline-flex items-center gap-2 font-display text-2xl text-foreground transition-opacity hover:opacity-60"
            >
              <Mail size={20} />
              music@thursdayjam.rocks
            </a>
          </motion.div>
        </div>

        {/* Bottom Links */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-20 pt-8 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-8"
        >
          <p className="font-body text-base text-muted-foreground">
            © 2018 – {new Date().getFullYear()} Thursday Jam. Berlin, Germany.
          </p>
          
          <a
            href="https://soundcloud.com/user-33747841"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground"
          >
            Listen on SoundCloud
            <ArrowUpRight size={12} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </motion.div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
